import { CardId } from './card';
import { FareType } from './fareType';

const EXPIRES_SOON_THRESHOLD = 5 * 24 * 3600 * 1000; // ms

/**
 * The current status of a fare.
 *
 * A fare is valid for a certain period of time. Fares expire
 * if they are not used after a period of time from their purchase,
 * or after another period of time after their first use.
 */
enum FareStatus {
  /**
   * The fare has not being used yet, and will expire if not
   * used after a period of time.
   */
  Inactive = 'Inactive',
  /**
   * The fare has been used at least once and will expire
   * in a set period of time.
   */
  Active = 'Active',
  /**
   * The fare is inactive or active and is going to expire
   * soon, i.e. its expiration date is within {@link EXPIRES_SOON_THRESHOLD}
   * ms.
   */
  ExpiresSoon = 'ExpiresSoon'
}

/**
 * Returns {@code true} if and only if the given date is within
 * {@link EXPIRES_SOON_THRESHOLD} ms. If the given date has already
 * passed the expiry date, an error is thrown.
 *
 * @param date the date to check
 * @param expiryDate the expiration date
 */
function expiresSoon(date: number, expiryDate: number): boolean {
  if (expiryDate < date) {
    throw new Error('Date has already expired');
  }
  return expiryDate - date <= EXPIRES_SOON_THRESHOLD;
}

export type Fare = {
  id: string;
  type: FareType;
  status: FareStatus;
  purchasedOn: Date;
  firstUsedOn: Date | null;
  expiresOn: Date;
};

const FARES_URL: string = (() => {
  const url = process.env.REACT_APP_FARES_URL;
  if (url === undefined) {
    throw new Error('`STATUS_URL` environment variable is not set');
  }
  console.info(`Fare status query URL: ${url}`);
  return url;
})();

export type FaresQueryResponse = {
  found: boolean;
  fares: Fare[];
};

enum APIError {
  MalformedXml = 'MalformedXml',
  MissingElement = 'MissingElement',
  MissingAttribute = 'MissingAttribute',
  InvalidAttribute = 'InvalidAttribute',
  InvalidCard = 'InvalidCard',
  UnknownCard = 'UnknownCard',
  UnknownCode = 'UnknownCode',
  Other = 'Other'
}

type APIResponse = {
  error: boolean;
  errorType: APIError | null;
  data?: Fare[];
};

const errorMessages: Record<APIError, string> = {
  MalformedXml: 'The response received from the CRTM servers is malformed.',
  MissingElement:
    'The response received from the CRTM servers is missing an element.',
  MissingAttribute:
    'An element in the response received from the CRTM servers is missing an attribute.',
  InvalidAttribute:
    'An element in the response received from the CRTM servers has an invalid attribute value.',
  InvalidCard: 'The given card ID is invalid.',
  UnknownCard: 'The given card ID is not registered.',
  UnknownCode: 'Received an unknown error code from the CRTM servers.',
  Other: 'An unknown error occurred.'
};

async function getFares(
  card: CardId,
  signal?: AbortSignal
): Promise<FaresQueryResponse> {
  const url = `${FARES_URL}?cardType=${card.type}&cardNumber=${card.number}`;
  const response = await fetch(url, { signal });
  const data: APIResponse = await response.json();

  if (data.error) {
    const type = data.errorType as APIError;
    if (type === APIError.UnknownCard) {
      return { found: false, fares: [] };
    } else {
      const message = errorMessages[type];
      throw new Error(message);
    }
  }

  const { data: fares } = data;
  if (fares === undefined) {
    throw new Error('Missing `data` array in API response');
  }

  return {
    found: true,
    fares
  };
}

export { FareStatus, expiresSoon, getFares };
